import { createApi } from "@reduxjs/toolkit/query/react";
import { useDispatch } from "react-redux";

import {
    LendVaultStatsResponse,
    UserExternalRewardsFilter,
    UserLendingVaultFilter,
    UserVaultExternalRewardsPnlResponse,
    UserVaultStakePositionsResponse,
    UserPositionDepositTermsResponse,
    UserPositionDepositTermsParams
} from "../types";
import { abfSerializeQueryArgs, marketsBaseQuery } from "./util";
import {
    ROUTE_GET_LENDING_USER_VAULT_POSITIONS,
    ROUTE_GET_USER_EXTERNAL_REWARDS_PNL,
    ROUTE_GET_USER_LENDING_VAULTS_ACTIVE_STATS,
    ROUTE_GET_USER_POSITION_PNL,
    ROUTE_GET_USER_GENESIS_ACCESS
} from "../constants";

const LENDING_VAULT_POSITION_TAG = "LendingVault";

export const lendingVaultApi = createApi({
    reducerPath: "abfLendingVaultApi",
    baseQuery: marketsBaseQuery,
    serializeQueryArgs: abfSerializeQueryArgs,

    tagTypes: [LENDING_VAULT_POSITION_TAG],

    endpoints: (builder) => ({
        lendingVaultPositions: builder.query<UserVaultStakePositionsResponse, UserLendingVaultFilter>({
            providesTags: [LENDING_VAULT_POSITION_TAG],
            query(params) {
                return {
                    url: ROUTE_GET_LENDING_USER_VAULT_POSITIONS,
                    method: "POST",
                    body: params
                };
            }
        }),
        lendVaultPositionStats: builder.query<LendVaultStatsResponse, void>({
            providesTags: [LENDING_VAULT_POSITION_TAG],
            query() {
                return {
                    url: ROUTE_GET_USER_LENDING_VAULTS_ACTIVE_STATS,
                    method: "GET"
                };
            }
        }),
        userPositionDepositTerms: builder.query<UserPositionDepositTermsResponse, UserPositionDepositTermsParams>({
            providesTags: [LENDING_VAULT_POSITION_TAG],
            query(params) {
                return {
                    url: ROUTE_GET_USER_POSITION_PNL,
                    method: "POST",
                    body: params
                };
            }
        }),
        userVaultExternalRewardsPnl: builder.query<UserVaultExternalRewardsPnlResponse, UserExternalRewardsFilter>({
            providesTags: [LENDING_VAULT_POSITION_TAG],
            query(params) {
                return {
                    url: ROUTE_GET_USER_EXTERNAL_REWARDS_PNL,
                    method: "POST",
                    body: params
                };
            }
        }),
        userGenesisAccess: builder.query<boolean, void>({
            providesTags: [LENDING_VAULT_POSITION_TAG],
            query() {
                return {
                    url: ROUTE_GET_USER_GENESIS_ACCESS,
                    method: "GET"
                };
            }
        })
    })
});

export const {
    useLendingVaultPositionsQuery,
    useLendVaultPositionStatsQuery,
    useUserPositionDepositTermsQuery,
    useUserGenesisAccessQuery,
    useUserVaultExternalRewardsPnlQuery
} = lendingVaultApi;

export function useLendingVaultApi() {
    const dispatch = useDispatch();

    return {
        resetLendingVaultPositionsApi: () => dispatch(lendingVaultApi.util.invalidateTags([LENDING_VAULT_POSITION_TAG]))
    };
}
